import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { 
  Users,
  Phone,
  PhoneIncoming,
  Clock,
  Activity,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Pause,
  Play,
  Coffee,
  LogOut,
  Headphones,
  Target,
  Timer,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Progress } from '@easyflow/ui-frontend/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/voip/select';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { useSocket } from '@/contexts/SocketContext';
import { AgentStatusBadge, AgentStatusIcon } from '@/components/queue/AgentStatusBadge';
import { getAgentStateBgColor, getEffectiveAgentState } from '@/lib/queue-helpers';
import type {
  QueueAgentsResetOnBreakResult,
  QueueRealtimeDashboard as DashboardData,
  QueueSlaDashboard as SLADashboardData,
  QueueDashboardSummary as DashboardSummary,
  QueueDashboardQueue as QueueData,
  QueueDashboardAgentDetail as AgentDetail,
  QueueSlaQueue as SLAQueueData,
  QueueSlaSummary as SLASummary,
} from '@easyflow/shared';

// Wire payload dei socket real-time (shape lasca lato server: campi opzionali).
interface ActiveCallPayload {
  uuid?: string;
  status?: string;
  extension?: string;
  callee_number?: string;
  caller_number?: string;
  answered_by_extension?: string;
  [key: string]: unknown;
}

interface AgentStatePayload {
  extension?: string;
  state?: string;
  [key: string]: unknown;
}

// Payload di queue:update: dashboard completa oppure aggiornamento singola coda.
interface QueueUpdatePayload {
  summary?: DashboardSummary;
  queues?: QueueData[];
  queueId?: string;
  [key: string]: unknown;
}

export default function QueueDashboard() {
  const { user, token } = useAuth();
  const queryClient = useQueryClient();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedQueue, setSelectedQueue] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('realtime');
  const [slaThreshold, setSLAThreshold] = useState('20');
  const [selectedSLAQueue, setSelectedSLAQueue] = useState<string | null>(null);
  const [isResettingAgents, setIsResettingAgents] = useState(false);
  const { isConnected: socketConnected, emit, on, off, socketVersion, joinRoom, leaveRoom } = useSocket();
  const activeCallsRef = useRef<ActiveCallPayload[]>([]);

  const { data: realtimeDashboardData, isLoading: realtimeLoading, refetch: refetchRealtime } = useQuery<DashboardData | null>({
    queryKey: ['queue-realtime-dashboard'],
    queryFn: async () => {
      const data = await apiClient.get<DashboardData>('/voip/queues/dashboard/realtime');
      return data.success && data.data ? data.data : null;
    },
    enabled: !!user && activeTab === 'realtime',
    staleTime: 10 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: !socketConnected && autoRefresh && activeTab !== 'sla' ? 15000 : false,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (realtimeDashboardData) {
      setDashboard(realtimeDashboardData);
    }
  }, [realtimeDashboardData]);

  const loadDashboard = useCallback(async () => {
    refetchRealtime();
  }, [refetchRealtime]);

  const { data: slaDashboard = null, isLoading: slaLoading, refetch: refetchSLA } = useQuery<SLADashboardData | null>({
    queryKey: ['queue-sla-dashboard', slaThreshold],
    queryFn: async () => {
      const data = await apiClient.get<SLADashboardData>(`/voip/queues/dashboard/sla?threshold=${slaThreshold}`);
      return data.success && data.data ? data.data : null;
    },
    enabled: !!user && activeTab === 'sla',
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: autoRefresh && activeTab === 'sla' ? 30000 : false,
    placeholderData: keepPreviousData,
  });

  const loadSLADashboard = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['queue-sla-dashboard', slaThreshold] });
  }, [queryClient, slaThreshold]);

  useEffect(() => {
    if (!user || !socketConnected) {
      console.log('[QueueDashboard] Waiting for socket connection...');
      return;
    }

    console.log('[QueueDashboard] Using shared socket (version:', socketVersion, ')');
    emit('queue:subscribe');
    const callsRoom = `tenant:${user?.tenant_id}:calls`;
    const switchboardRoom = `tenant:${user?.tenant_id}:switchboard`;
    joinRoom(switchboardRoom);
    joinRoom(callsRoom);

    const handleCallsUpdate = (calls: ActiveCallPayload[]) => {
      activeCallsRef.current = calls || [];
    };

    const handleCallCreated = (call: ActiveCallPayload) => {
      activeCallsRef.current = [...activeCallsRef.current.filter(c => c.uuid !== call.uuid), call];
    };

    const handleCallUpdated = (call: ActiveCallPayload) => {
      activeCallsRef.current = activeCallsRef.current.map(c => c.uuid === call.uuid ? call : c);
    };

    const handleCallAnswered = (call: ActiveCallPayload) => {
      activeCallsRef.current = activeCallsRef.current.map(c =>
        c.uuid === call.uuid ? { ...c, ...call, status: call.status || 'answered' } : c
      );
    };

    const handleCallBridged = (call: ActiveCallPayload) => {
      activeCallsRef.current = activeCallsRef.current.map(c =>
        c.uuid === call.uuid ? { ...c, ...call, status: 'bridged' } : c
      );
    };

    const handleCallEnded = (data: { uuid: string }) => {
      activeCallsRef.current = activeCallsRef.current.filter(c => c.uuid !== data.uuid);
    };

    const handleAgentState = (data: AgentStatePayload) => {
      console.log('[QueueDashboard] Received queue:agent-state', data);
      if (activeTab !== 'realtime') return;

      // Boundary: lo stato dal wire è string; qui lo trattiamo come AgentDetail['state'].
      let effectiveState = data.state as AgentDetail['state'] | undefined;
      if (data.state === 'Waiting' || data.state === 'Available') {
        const currentCalls = activeCallsRef.current;
        const hasActiveCall = currentCalls.some((call) =>
          call.callee_number === data.extension ||
          call.caller_number === data.extension ||
          call.extension === data.extension ||
          call.answered_by_extension === data.extension
        );
        if (hasActiveCall) {
          console.log('[QueueDashboard] 🛡️ Agent', data.extension, 'has active call - keeping "In a queue call" instead of', data.state);
          effectiveState = 'In a queue call';
        }
      }
      
      setDashboard(prev => {
        if (!prev?.queues) return prev;
        
        const updatedQueues = prev.queues.map((q) => {
          // Il wire può usare chiavi alternative per il numero interno dell'agente.
          const updatedAgentsDetail = q.agents_detail?.map((agent: AgentDetail & { extensionNumber?: string; extension_number?: string }) => {
            const isMatch = agent.extension_id === data.extension ||
                            agent.extensionNumber === data.extension ||
                            agent.extension_number === data.extension;
            if (isMatch && effectiveState) {
              return { ...agent, state: effectiveState };
            }
            return agent;
          });
          
          if (!updatedAgentsDetail) return q;
          
          const agentsSummary: QueueData['agents'] = {
            total: updatedAgentsDetail.length,
            available: updatedAgentsDetail.filter((a) => 
              (a.status === 'Available' || !a.status || (a.status as string) === 'Waiting') && 
              a.state !== 'In a queue call' && a.state !== 'Receiving'
            ).length,
            on_break: updatedAgentsDetail.filter((a) => a.status === 'On Break').length,
            logged_out: updatedAgentsDetail.filter((a) => a.status === 'Logged Out').length,
            on_call: updatedAgentsDetail.filter((a) => a.state === 'In a queue call').length,
          };
          
          return { ...q, agents_detail: updatedAgentsDetail, agents: agentsSummary };
        });
        
        const summary = {
          ...prev.summary,
          available_agents: updatedQueues.reduce((sum, q) => sum + (q.agents?.available || 0), 0),
          total_agents: updatedQueues.reduce((sum, q) => sum + (q.agents?.total || 0), 0),
        };
        
        return { ...prev, queues: updatedQueues, summary, timestamp: new Date().toISOString() };
      });
    };

    const handleQueueUpdate = (data: QueueUpdatePayload) => {
      console.log('[QueueDashboard] Received queue:update', data);
      if (activeTab === 'realtime') {
        setDashboard(prev => {
          // If data has the full structure { summary, queues }, replace entirely
          // Boundary: quando summary+queues sono presenti il payload È la dashboard completa.
          if (data?.summary && data?.queues) {
            return data as unknown as DashboardData;
          }
          
          // Otherwise it's a single queue update - merge it into existing dashboard
          if (data?.queueId && prev?.queues) {
            const updatedQueues = prev.queues.map((q) => 
              q.id === data.queueId || (q as QueueData & { queueId?: string }).queueId === data.queueId ? { ...q, ...data } : q
            );
            
            // Recalculate summary based on updated queues
            const summary = {
              ...prev.summary,
              available_agents: updatedQueues.reduce((sum, q) => sum + (q.agents?.available || 0), 0),
              total_agents: updatedQueues.reduce((sum, q) => sum + (q.agents?.total || 0), 0),
            };
            
            return { ...prev, queues: updatedQueues, summary };
          }
          
          return prev;
        });
      }
    };

    const handleQueueError = (error: { message: string }) => {
      console.error('[QueueDashboard] Socket error:', error.message);
    };

    on('queue:update', handleQueueUpdate);
    on('queue:agent-state', handleAgentState);
    on('queue:error', handleQueueError);
    on('calls:update', handleCallsUpdate);
    on('call:created', handleCallCreated);
    on('call:updated', handleCallUpdated);
    on('call:answered', handleCallAnswered);
    on('call:bridged', handleCallBridged);
    on('call:ended', handleCallEnded);

    return () => {
      emit('queue:unsubscribe');
      leaveRoom(switchboardRoom);
      leaveRoom(callsRoom);
      off('queue:update', handleQueueUpdate);
      off('queue:agent-state', handleAgentState);
      off('queue:error', handleQueueError);
      off('calls:update', handleCallsUpdate);
      off('call:created', handleCallCreated);
      off('call:updated', handleCallUpdated);
      off('call:answered', handleCallAnswered);
      off('call:bridged', handleCallBridged);
      off('call:ended', handleCallEnded);
      activeCallsRef.current = [];
    };
  }, [user, socketConnected, socketVersion, activeTab, emit, on, off, joinRoom, leaveRoom]);


  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatTalkTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  const getAnswerRate = (stats: QueueData['stats_today']) => {
    if (stats.total === 0) return 100;
    return Math.round((stats.answered / stats.total) * 100);
  };

  const handleResetOnBreakAgents = async () => {
    setIsResettingAgents(true);
    try {
      const data = await apiClient.post<QueueAgentsResetOnBreakResult>('/voip/queues/agents/reset-on-break');
      if (data.success) {
        console.log(`✅ Reset ${data.data?.reset || 0} agents to Available`);
        await loadDashboard();
      }
    } catch (error) {
      console.error('❌ Error resetting agents:', error);
    } finally {
      setIsResettingAgents(false);
    }
  };


  const getSLAColor = (sla: number) => {
    if (sla >= 80) return 'text-green-600';
    if (sla >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSLABgColor = (sla: number) => {
    if (sla >= 80) return 'bg-green-100 border-green-200';
    if (sla >= 60) return 'bg-yellow-100 border-yellow-200';
    return 'bg-red-100 border-red-200';
  };

  const getSLAIcon = (sla: number) => {
    if (sla >= 80) return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    if (sla >= 60) return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    return <XCircle className="h-5 w-5 text-red-500" />;
  };

  if (!dashboard && activeTab === 'realtime') {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const selectedQueueData = selectedQueue 
    ? dashboard?.queues.find(q => q.id === selectedQueue) 
    : null;

  const selectedSLAQueueData = selectedSLAQueue
    ? slaDashboard?.queues.find(q => q.queue_id === selectedSLAQueue)
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Queue Dashboard</h1>
          <p className="text-muted-foreground">
            Monitoraggio real-time code e SLA
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge 
            variant={socketConnected ? "default" : "secondary"} 
            className={`text-xs ${socketConnected ? 'bg-green-500 hover:bg-green-600' : 'bg-muted-foreground'}`}
          >
            {socketConnected ? <Wifi className="h-3 w-3 mr-1" /> : <WifiOff className="h-3 w-3 mr-1" />}
            {socketConnected ? 'Real-time 5s' : 'Polling 15s'}
          </Badge>
          <Button 
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
            {autoRefresh ? 'Pausa' : 'Auto'}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={activeTab === 'realtime' ? loadDashboard : loadSLADashboard} 
            disabled={realtimeLoading || slaLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${realtimeLoading || slaLoading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="realtime" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Real-time
          </TabsTrigger>
          <TabsTrigger value="sla" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            SLA Dashboard
          </TabsTrigger>
        </TabsList>

        <TabsContent value="realtime" className="space-y-6 mt-4">
          {dashboard && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-600 font-medium">Code Attive</p>
                        <p className="text-2xl font-bold text-blue-700">{dashboard.summary.active_queues}</p>
                      </div>
                      <Activity className="h-8 w-8 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-green-600 font-medium">Agenti Disponibili</p>
                        <p className="text-2xl font-bold text-green-700">
                          {dashboard.summary.available_agents}/{dashboard.summary.total_agents}
                        </p>
                      </div>
                      <Users className="h-8 w-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className={`bg-gradient-to-br ${dashboard.summary.total_waiting_calls > 0 ? 'from-orange-50 to-orange-100 border-orange-200' : 'from-muted to-muted border-border'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-xs font-medium ${dashboard.summary.total_waiting_calls > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>
                          In Attesa
                        </p>
                        <p className={`text-2xl font-bold ${dashboard.summary.total_waiting_calls > 0 ? 'text-orange-700 animate-pulse' : 'text-muted-foreground'}`}>
                          {dashboard.summary.total_waiting_calls}
                        </p>
                      </div>
                      <PhoneIncoming className={`h-8 w-8 ${dashboard.summary.total_waiting_calls > 0 ? 'text-orange-400' : 'text-muted-foreground'}`} />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-purple-600 font-medium">Chiamate Oggi</p>
                        <p className="text-2xl font-bold text-purple-700">{dashboard.summary.total_calls_today}</p>
                      </div>
                      <Phone className="h-8 w-8 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Risposte</p>
                        <p className="text-2xl font-bold text-emerald-700">{dashboard.summary.answered_today}</p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-emerald-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-red-600 font-medium">Abbandonate</p>
                        <p className="text-2xl font-bold text-red-700">{dashboard.summary.abandoned_today}</p>
                      </div>
                      <TrendingDown className="h-8 w-8 text-red-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="col-span-2 bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-indigo-600 font-medium">Tasso Risposta</p>
                        <p className="text-2xl font-bold text-indigo-700">
                          {dashboard.summary.total_calls_today > 0 
                            ? Math.round((dashboard.summary.answered_today / dashboard.summary.total_calls_today) * 100) 
                            : 100}%
                        </p>
                      </div>
                      <Progress 
                        value={dashboard.summary.total_calls_today > 0 
                          ? (dashboard.summary.answered_today / dashboard.summary.total_calls_today) * 100 
                          : 100} 
                        className="w-24 h-2"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* SEZIONE 2: Selettore Code - Barra orizzontale a tutta larghezza */}
              <div className="space-y-3">
                <h2 className="text-xl font-semibold">Seleziona Coda</h2>
                <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-lg border">
                  {dashboard.queues.map((queue) => (
                    <button
                      key={queue.id}
                      onClick={() => setSelectedQueue(selectedQueue === queue.id ? null : queue.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                        selectedQueue === queue.id 
                          ? 'bg-blue-600 text-white shadow-md' 
                          : 'bg-card border border-border hover:bg-muted text-muted-foreground'
                      } ${!queue.enabled ? 'opacity-60' : ''}`}
                    >
                      <span className="font-medium">{queue.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        selectedQueue === queue.id 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        Ext. {queue.extension}
                      </span>
                      {queue.waiting_calls > 0 && (
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-500 text-white animate-pulse">
                          <PhoneIncoming className="h-3 w-3" />
                          {queue.waiting_calls}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        queue.agents.available > 0
                          ? 'bg-green-100 text-green-700'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {queue.agents.available}/{queue.agents.total}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* SEZIONE 3: Cards Code a tutta larghezza */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Code</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {dashboard.queues.map((queue) => (
                    <Card 
                      key={queue.id} 
                      className={`cursor-pointer transition-all hover:shadow-md ${selectedQueue === queue.id ? 'ring-2 ring-blue-500 shadow-lg' : ''} ${!queue.enabled ? 'opacity-60' : ''}`}
                      onClick={() => setSelectedQueue(selectedQueue === queue.id ? null : queue.id)}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base">{queue.name}</CardTitle>
                            <CardDescription>Ext. {queue.extension}</CardDescription>
                          </div>
                          <Badge variant={queue.enabled ? 'default' : 'secondary'}>
                            {queue.enabled ? 'Attiva' : 'Inattiva'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className={`p-2 rounded ${queue.waiting_calls > 0 ? 'bg-orange-100' : 'bg-muted'}`}>
                            <p className={`text-lg font-bold ${queue.waiting_calls > 0 ? 'text-orange-600 animate-pulse' : 'text-muted-foreground'}`}>
                              {queue.waiting_calls}
                            </p>
                            <p className="text-xs text-muted-foreground">In Attesa</p>
                          </div>
                          <div className="p-2 rounded bg-green-100">
                            <p className="text-lg font-bold text-green-600">{queue.agents.available}</p>
                            <p className="text-xs text-muted-foreground">Disponibili</p>
                          </div>
                          <div className="p-2 rounded bg-blue-100">
                            <p className="text-lg font-bold text-blue-600">{queue.agents.on_call}</p>
                            <p className="text-xs text-muted-foreground">In Chiamata</p>
                          </div>
                        </div>

                        {queue.waiting_calls > 0 && queue.longest_wait_seconds > 0 && (
                          <div className="flex items-center text-sm text-orange-600">
                            <Clock className="h-4 w-4 mr-1" />
                            Attesa max: {formatTime(queue.longest_wait_seconds)}
                          </div>
                        )}

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Oggi: {queue.stats_today.answered}/{queue.stats_today.total}</span>
                          <div className="flex items-center">
                            <Progress value={getAnswerRate(queue.stats_today)} className="w-16 h-2 mr-2" />
                            <span className="text-xs text-muted-foreground">{getAnswerRate(queue.stats_today)}%</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>Attesa media: {formatTime(queue.stats_today.avg_wait_time)}</span>
                          <span>{queue.agents.total} agenti</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* SEZIONE 4: Pannello Agenti - Appare sotto quando si seleziona una coda */}
              {selectedQueueData && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Agenti - {selectedQueueData.name}
                    </h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedQueue(null)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Chiudi
                    </Button>
                  </div>
                  
                  <Card>
                    <CardContent className="p-4 space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <Headphones className="h-3 w-3 mr-1" />
                            {selectedQueueData.agents.available} Disponibili
                          </Badge>
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                            <Coffee className="h-3 w-3 mr-1" />
                            {selectedQueueData.agents.on_break} In Pausa
                          </Badge>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <Phone className="h-3 w-3 mr-1" />
                            {selectedQueueData.agents.on_call} In Chiamata
                          </Badge>
                          <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
                            <LogOut className="h-3 w-3 mr-1" />
                            {selectedQueueData.agents.logged_out} Disconnessi
                          </Badge>
                        </div>
                        {selectedQueueData.agents.on_break > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleResetOnBreakAgents}
                            disabled={isResettingAgents}
                            className="text-yellow-700 border-yellow-300 hover:bg-yellow-50"
                          >
                            {isResettingAgents ? (
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <Play className="h-3 w-3 mr-1" />
                            )}
                            Riattiva Agenti
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {selectedQueueData.agents_detail.map((agent) => (
                          <div 
                            key={agent.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${getAgentStateBgColor(getEffectiveAgentState(agent))}`}
                          >
                            <div className="flex items-center space-x-3">
                              <AgentStatusIcon status={agent.status} />
                              <div>
                                <p className="font-medium text-sm">{agent.agent_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {agent.calls_answered} chiamate | {formatTalkTime(agent.talk_time)}
                                </p>
                              </div>
                            </div>
                            <AgentStatusBadge state={agent.state} status={agent.status} showIcon={false} size="sm" />
                          </div>
                        ))}
                      </div>

                      {selectedQueueData.agents_detail.length === 0 && (
                        <div className="text-center text-muted-foreground py-8">
                          <Users className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                          <p>Nessun agente in questa coda</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="sla" className="space-y-6 mt-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">SLA Threshold:</label>
              <Select value={slaThreshold} onValueChange={(v) => setSLAThreshold(v)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 secondi</SelectItem>
                  <SelectItem value="15">15 secondi</SelectItem>
                  <SelectItem value="20">20 secondi</SelectItem>
                  <SelectItem value="30">30 secondi</SelectItem>
                  <SelectItem value="45">45 secondi</SelectItem>
                  <SelectItem value="60">60 secondi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {slaDashboard && (
              <span className="text-xs text-muted-foreground">
                Ultimo aggiornamento: {new Date(slaDashboard.timestamp).toLocaleTimeString()}
              </span>
            )}
          </div>

          {slaLoading && !slaDashboard ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : slaDashboard ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <Card className={`col-span-2 ${getSLABgColor(slaDashboard.summary.overall_sla)}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium opacity-70">SLA Complessivo</p>
                        <p className={`text-3xl font-bold ${getSLAColor(slaDashboard.summary.overall_sla)}`}>
                          {slaDashboard.summary.overall_sla}%
                        </p>
                        <p className="text-xs opacity-60">
                          Risposte entro {slaDashboard.summary.sla_threshold}s
                        </p>
                      </div>
                      {getSLAIcon(slaDashboard.summary.overall_sla)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-600 font-medium">Tasso Risposta</p>
                        <p className="text-2xl font-bold text-blue-700">{slaDashboard.summary.overall_answer_rate}%</p>
                      </div>
                      <TrendingUp className="h-6 w-6 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-purple-600 font-medium">Totale Chiamate</p>
                        <p className="text-2xl font-bold text-purple-700">{slaDashboard.summary.total_calls}</p>
                      </div>
                      <Phone className="h-6 w-6 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-green-600 font-medium">Entro SLA</p>
                        <p className="text-2xl font-bold text-green-700">{slaDashboard.summary.answered_within_sla}</p>
                      </div>
                      <CheckCircle2 className="h-6 w-6 text-green-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-red-600 font-medium">Abbandonate</p>
                        <p className="text-2xl font-bold text-red-700">{slaDashboard.summary.abandoned_calls}</p>
                      </div>
                      <XCircle className="h-6 w-6 text-red-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* SEZIONE 2 SLA: Selettore Code - Barra orizzontale a tutta larghezza */}
              <div className="space-y-3">
                <h2 className="text-xl font-semibold">Seleziona Coda</h2>
                <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-lg border">
                  {slaDashboard.queues.map((queue) => (
                    <button
                      key={queue.queue_id}
                      onClick={() => setSelectedSLAQueue(selectedSLAQueue === queue.queue_id ? null : queue.queue_id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                        selectedSLAQueue === queue.queue_id 
                          ? 'bg-blue-600 text-white shadow-md' 
                          : 'bg-card border border-border hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      <span className="font-medium">{queue.queue_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        selectedSLAQueue === queue.queue_id 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        Ext. {queue.extension}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                        queue.sla_percentage >= 80 
                          ? 'bg-green-100 text-green-700' 
                          : queue.sla_percentage >= 60 
                            ? 'bg-yellow-100 text-yellow-700' 
                            : 'bg-red-100 text-red-700'
                      }`}>
                        {queue.sla_percentage}%
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* SEZIONE 3 SLA: Cards Code a tutta larghezza */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Performance Code
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {slaDashboard.queues.map((queue) => (
                    <Card 
                      key={queue.queue_id}
                      className={`cursor-pointer transition-all hover:shadow-md ${selectedSLAQueue === queue.queue_id ? 'ring-2 ring-blue-500 shadow-lg' : ''} ${getSLABgColor(queue.sla_percentage)}`}
                      onClick={() => setSelectedSLAQueue(selectedSLAQueue === queue.queue_id ? null : queue.queue_id)}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base">{queue.queue_name}</CardTitle>
                            <CardDescription>Ext. {queue.extension}</CardDescription>
                          </div>
                          <div className="flex items-center gap-2">
                            {getSLAIcon(queue.sla_percentage)}
                            <span className={`text-2xl font-bold ${getSLAColor(queue.sla_percentage)}`}>
                              {queue.sla_percentage}%
                            </span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-4 gap-2 text-center text-xs">
                          <div className="p-2 rounded bg-muted/50">
                            <p className="font-bold text-muted-foreground">{queue.total_calls}</p>
                            <p className="text-muted-foreground">Totali</p>
                          </div>
                          <div className="p-2 rounded bg-muted/50">
                            <p className="font-bold text-green-600">{queue.answered_within_sla}</p>
                            <p className="text-muted-foreground">In SLA</p>
                          </div>
                          <div className="p-2 rounded bg-muted/50">
                            <p className="font-bold text-blue-600">{queue.answered_calls}</p>
                            <p className="text-muted-foreground">Risposte</p>
                          </div>
                          <div className="p-2 rounded bg-muted/50">
                            <p className="font-bold text-red-600">{queue.abandoned_calls}</p>
                            <p className="text-muted-foreground">Perse</p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1">
                            <Timer className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">Attesa media: {formatTime(queue.avg_wait_time)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">P90: {formatTime(queue.p90_wait_time)}</span>
                          </div>
                        </div>

                        <Progress value={queue.sla_percentage} className="h-2" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* SEZIONE 4 SLA: Pannello Dettagli - Appare sotto quando si seleziona una coda */}
              {selectedSLAQueueData && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Dettagli SLA - {selectedSLAQueueData.queue_name}
                    </h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedSLAQueue(null)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Chiudi
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Trend Orario SLA */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <BarChart3 className="h-4 w-4" />
                          Trend Orario SLA
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedSLAQueueData.hourly_stats.length > 0 ? (
                          <div className="space-y-2 max-h-[250px] overflow-y-auto">
                            {selectedSLAQueueData.hourly_stats.map((stat) => (
                              <div key={stat.hour} className="flex items-center gap-2">
                                <span className="w-12 text-xs text-muted-foreground">{stat.hour}:00</span>
                                <div className="flex-1">
                                  <Progress 
                                    value={stat.sla_pct} 
                                    className={`h-4 ${stat.sla_pct >= 80 ? '[&>div]:bg-green-500' : stat.sla_pct >= 60 ? '[&>div]:bg-yellow-500' : '[&>div]:bg-red-500'}`}
                                  />
                                </div>
                                <span className={`w-12 text-xs font-medium ${getSLAColor(stat.sla_pct)}`}>
                                  {stat.sla_pct}%
                                </span>
                                <span className="w-16 text-xs text-muted-foreground">
                                  {stat.within_sla}/{stat.total}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-muted-foreground py-4 text-sm">
                            Nessun dato orario disponibile
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Performance Agenti */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Performance Agenti
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedSLAQueueData.agent_performance.length > 0 ? (
                          <div className="space-y-2 max-h-[250px] overflow-y-auto">
                            {selectedSLAQueueData.agent_performance.map((agent, idx) => (
                              <div 
                                key={idx}
                                className="flex items-center justify-between p-2 rounded border bg-muted"
                              >
                                <div className="flex items-center gap-2">
                                  <AgentStatusIcon status={agent.status} />
                                  <div>
                                    <p className="text-sm font-medium">{agent.agent_name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      AHT: {formatTime(agent.avg_handle_time)}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-bold text-blue-600">{agent.calls_today}</p>
                                  <p className="text-xs text-muted-foreground">oggi</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-muted-foreground py-4 text-sm">
                            Nessun agente in questa coda
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Metriche Attesa */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Timer className="h-4 w-4" />
                          Metriche Attesa
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3 text-center">
                          <div className="p-3 rounded bg-muted">
                            <p className="text-lg font-bold">{formatTime(selectedSLAQueueData.avg_wait_time)}</p>
                            <p className="text-xs text-muted-foreground">Media</p>
                          </div>
                          <div className="p-3 rounded bg-muted">
                            <p className="text-lg font-bold">{formatTime(selectedSLAQueueData.median_wait_time)}</p>
                            <p className="text-xs text-muted-foreground">Mediana</p>
                          </div>
                          <div className="p-3 rounded bg-muted">
                            <p className="text-lg font-bold">{formatTime(selectedSLAQueueData.p90_wait_time)}</p>
                            <p className="text-xs text-muted-foreground">90° Percentile</p>
                          </div>
                          <div className="p-3 rounded bg-muted">
                            <p className="text-lg font-bold">{formatTime(selectedSLAQueueData.max_wait_time)}</p>
                            <p className="text-xs text-muted-foreground">Massima</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Target className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                <p>Caricamento dati SLA...</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
